/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, { useState, forwardRef, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Badge,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Stack,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ExpandLess, ExpandMore, FilterAlt } from '@mui/icons-material';

import Locations from '../Locations';
import GroupsList from '../GroupsList';
import ModalWithActions from '../../ModalWithActions';
import StackNavigation from '../StackNavigation';
import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { useBuildingDetails } from '../../../context/BuildingContext';
import LocationsList from '../LocationsList';
import MenuOptions from './MenuOptions';
import SensorCounter from './SensorCounter';
import { useGeneralContext } from '../../../context/GeneralContext';
import { openAnimated } from '../../../redux/actions/sideDrawerActions';
import CustomTooltip from '../../shared/atoms/CustomTooltip';
import {
  initLocFilterData,
  useLocationsCacheContext,
} from '../../../context/LocationsCacheContext';
import RefreshButton from '../../shared/molecules/RefreshButton';
import CustomButton from '../../shared/atoms/CustomButton';
import GlobalSearch from '../../shared/atoms/GlobalSearch';
import UserGroupSelect from '../../shared/molecules/UserGroupSelect';
import StatusSelect from '../../shared/molecules/StatusSelect';
import { useIsMobile } from '../../../hooks/useIsMobile';

const LOCATIONS_CONTEXT = 0;
const GROUPS_CONTEXT = 1;
const LOCATION_CREATION = 2;
const GROUP_CREATION = 4;

const InitialPage = props => {
  return <Locations {...props} />;
};

const drawerWidth = 280;

const openedMixin = theme => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: 'transparent',
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw + 1px)',
  },
});

const closedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 0,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
  backgroundColor: 'transparent',
});

const MiniDrawer = styled(Drawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const MainList = forwardRef((props, ref) => {
  const smallHeight = useMediaQuery('(max-height:800px)');
  const smallerHeight = useMediaQuery('(max-height:500px)');
  const isOpen = useSelector(store => store.sideDrawerState.isOpen);
  const [context, setContext] = useState(LOCATIONS_CONTEXT);
  const [creationContext, setCreationContext] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const isMobile = useIsMobile();
  const history = useHistory();
  const { data: userPermissions } = useRolePermissions();
  const { setHideDrawers } = useBuildingDetails();
  const { t } = useTranslation();
  const {
    sideMenuForm,
    setSideMenuForm,
    menuExpanded,
    setMenuExpanded,
    isMapPage,
  } = useGeneralContext();
  const {
    refresh,
    refreshMap,
    dataUpdatedAt,
    locations,
    assets,
    buildings,
    filterData,
    setFilterData,
    isFilterApplied,
  } = useLocationsCacheContext();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isOpen) {
      setContext(LOCATIONS_CONTEXT);
      setCreationContext(null);
      setShowModal(false);
      setSideMenuForm(false);
    }
  }, [isOpen, setSideMenuForm]);

  useEffect(() => {
    if (!isOpen && isMapPage) {
      dispatch(openAnimated());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelCreation = () => {
    setCreationContext(null);
    setShowModal(false);
    setSideMenuForm(false);
  };

  const renderContextComponent = () => {
    if (context === GROUPS_CONTEXT && creationContext === GROUP_CREATION)
      return <LocationsList goBack={cancelCreation} />;
    if (context === LOCATIONS_CONTEXT)
      return (
        <StackNavigation
          InitialPage={InitialPage}
          onPopPage={cancelCreation}
          creationContext={creationContext}
          setHideDrawers={setHideDrawers}
          filterData={filterData}
        />
      );

    if (context === GROUPS_CONTEXT)
      return <GroupsList goBack={cancelCreation} />;

    return null;
  };

  const handleOpenLocations = () => {
    if (isMapPage) {
      setCreationContext(LOCATION_CREATION);
      setSideMenuForm(true);
    } else {
      setShowModal(true);
    }
  };

  const create = () => {
    switch (context) {
      case GROUPS_CONTEXT:
        setCreationContext(GROUP_CREATION);
        setSideMenuForm(true);
        break;
      case LOCATIONS_CONTEXT:
        handleOpenLocations();
        break;
      default:
        break;
    }
  };

  const hideModal = () => {
    setShowModal(false);
    setSideMenuForm(false);
  };

  const goToMap = () => {
    history.push('/portal/maps');
  };

  const checkModalToShow = () => {
    if (!isMapPage)
      return (
        <ModalWithActions
          show={showModal}
          modalTitle={t('components.main_list.modal.create_location.title')}
          modalText={t(
            'components.main_list.modal.create_location.description',
          )}
          btActionText={t('components.main_list.modal.create_location.action')}
          btActionDisabled={false}
          action={goToMap}
          onHide={hideModal}
        />
      );
  };

  const searchOptions = useMemo(
    () => [
      ...locations.map(loc => ({
        value: loc.properties.id,
        label: loc.properties.name,
        type: 'LOC',
      })),
      ...buildings.map(b => ({
        value: b.properties.id,
        label: b.properties.name,
        type: 'BLD',
      })),
      ...assets
        .filter(a => a.properties.is_placed)
        .map(a => ({
          value: a.properties.id,
          label: a.properties.name,
          type: 'OAST',
        })),
    ],
    [assets, buildings, locations],
  );

  const formatRevisionDate = dateString => {
    const month = dateString.slice(0, 2);
    const day = dateString.slice(2, 4);
    const year = dateString.slice(4, 8);
    return [month, day, year].join('.');
  };

  return (
    <MiniDrawer
      variant="permanent"
      open={isOpen}
      ref={ref}
      data-testid="left-menu-drawer"
    >
      <Stack
        sx={{
          background: 'linear-gradient(90deg, #02112d 0%, #152648 100%)',
          marginTop: '50px',
          overflow: 'hidden',
          pointerEvents: 'all',
          height: '100%',
        }}
      >
        {checkModalToShow()}
        {isOpen && !sideMenuForm && userPermissions && (
          <Box
            sx={{
              width: '100%',
              color: 'white',
              padding: '10px 1rem 10px 1rem',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Stack width="100%" direction="row" alignItems="flex-end">
                <Typography variant="h6" paddingTop="5px" paddingRight="5px">
                  {t('components.main_list.filter.Locations')}
                </Typography>
              </Stack>

              <Box>
                <RefreshButton
                  title={t('components.main_list.filter.refresh_locations')}
                  dataUpdatedAt={dataUpdatedAt}
                  isRefreshing={refresh}
                  refreshAction={refreshMap}
                  color="white"
                />
                <CustomTooltip
                  title={
                    <Stack>
                      <Typography
                        variant="body2"
                        gutterBottom={isFilterApplied}
                      >
                        {t('components.main_list.filter.tooltip')}
                      </Typography>
                      {isFilterApplied && (
                        <CustomButton
                          onMouseDown={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            setFilterData(initLocFilterData);
                          }}
                        >
                          {t('components.autocomplete.clear')}
                        </CustomButton>
                      )}
                    </Stack>
                  }
                  placement="right"
                >
                  <IconButton onClick={() => setOpenFilter(prev => !prev)}>
                    <Badge
                      variant="dot"
                      invisible={!isFilterApplied}
                      color="primary"
                    >
                      <FilterAlt sx={{ color: 'white' }} />
                    </Badge>
                  </IconButton>
                </CustomTooltip>
              </Box>

              {userPermissions?.map?.manage && !isMobile && (
                <CustomTooltip
                  title={t('components.main_list.filter.add_location')}
                  placement="right"
                >
                  <Box>
                    <CustomButton
                      style={{
                        padding: 0,
                        height: '2.375rem',
                        width: '2.4375rem',
                      }}
                      onClick={create}
                      color="primary"
                    >
                      <i className="fas fa-plus" />
                    </CustomButton>
                  </Box>
                </CustomTooltip>
              )}
            </Stack>
            <Collapse in={openFilter} timeout="auto" unmountOnExit>
              <Stack paddingTop="10px">
                <GlobalSearch
                  list={searchOptions}
                  loading={refresh}
                  onSelected={value =>
                    setFilterData({
                      filter: value,
                      filterIds: new Set(value.map(v => v.value)),
                    })
                  }
                  multiple
                  noAdornment
                  external
                  externalFilter={filterData.filter}
                  label={t('components.main_list.filter.label')}
                />
                <Box paddingTop="10px">
                  <UserGroupSelect
                    value={Array.from(filterData.userGroup)}
                    disableFillAdmin
                    labelInside
                    hideSelectAll
                    setFieldValue={(_, value) =>
                      setFilterData({
                        userGroup: new Set(value),
                      })
                    }
                  />
                </Box>
                <Box paddingTop="10px">
                  <StatusSelect
                    labelInside
                    value={filterData.status || []}
                    handleChange={value =>
                      setFilterData({
                        status: value,
                      })
                    }
                  />
                </Box>
              </Stack>
            </Collapse>
          </Box>
        )}
        {renderContextComponent()}
        {!sideMenuForm && (
          <>
            <Box
              padding="0 10px 0 10px"
              display={smallerHeight ? 'none' : 'block'}
            >
              <Divider
                sx={{
                  '&::before, &::after': {
                    borderColor: theme => theme.palette.primary.main,
                    opacity: 1,
                  },
                  '& .MuiDivider-wrapper': {
                    px: 0,
                  },
                }}
              >
                <CustomTooltip
                  title={
                    menuExpanded
                      ? t('components.main_list.collapse')
                      : t('components.main_list.expand')
                  }
                  placement="right"
                  open={tooltipOpen}
                  onOpen={() => setTooltipOpen(true)}
                  onClose={() => setTooltipOpen(false)}
                  disableInteractive
                  leaveTouchDelay={0}
                >
                  <IconButton
                    size="small"
                    sx={{
                      backgroundColor: theme => theme.palette.primary.main,
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: theme => theme.palette.primary.dark,
                      },
                    }}
                    onClick={() => {
                      setMenuExpanded(prev => !prev);
                      setTooltipOpen(false);
                    }}
                  >
                    {menuExpanded ? (
                      <ExpandMore fontSize={isOpen ? 'medium' : 'small'} />
                    ) : (
                      <ExpandLess fontSize={isOpen ? 'medium' : 'small'} />
                    )}
                  </IconButton>
                </CustomTooltip>
              </Divider>
            </Box>
            <Collapse in={menuExpanded} timeout="auto" unmountOnExit>
              <MenuOptions smallHeight={smallHeight} />
              <Box
                padding="0 10px 0 10px"
                display={smallHeight ? 'none' : 'block'}
              >
                <Divider
                  sx={{
                    borderColor: theme => theme.palette.primary.main,
                    width: '100%',
                    borderBottomWidth: 1,
                    opacity: 1,
                  }}
                />
              </Box>
            </Collapse>
            <Box display={smallHeight ? 'none' : 'block'}>
              <SensorCounter />
            </Box>
            {isOpen && (
              <Box>
                <Stack
                  padding="0 1rem 1rem 1rem"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    Powered by CorGrid &reg;
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('modal.about.revision')}</strong>
                    {formatRevisionDate(process.env.REACT_APP_REVISION)}
                    &nbsp;
                    <strong>{t('modal.about.version')}</strong>
                    {process.env.REACT_APP_VERSION}
                  </Typography>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Stack>
    </MiniDrawer>
  );
});

export default MainList;
